/* eslint-disable camelcase */
import { all, put, takeEvery, select } from 'redux-saga/effects'
import { toast } from 'react-toastify'

import {
  ATTEMPT_MESSAGE_SEND_WITH_LOCATION,
  LOCATION_FETCHING,
  LOCATION_FETCH_FAIL,
  LOCATION_RESET,
  LOCATION_SET,
  MESSAGE_SEND_SUCCESS,
  MESSAGE_SEND_FAIL,
  MESSAGE_SUBMIT_ATTEMPT,
  ON_MESSAGE_SEND,
  SET_MESSAGES_TO_SEND,
  UPDATE_LOADING
} from '@redux/actions/types'

import { sendSuggestionAction } from '@redux/actions/message'

const getAuth = state => ({
  apiKey: state.auth.apiKey,
  hash: state.auth.hash
})

const getIsAwaitingLocation = state => state.message.isAwaitingLocation

const getLocation = state => state.location.currentLocation

const getMessagesToSend = state => state.message.messagesToSend

/**
 * Queues a message for sending and triggers location fetch if not already in progress.
 * @param action
 */
function * onAttemptMessageSendWithLocation(action) {
  yield put({
    type: UPDATE_LOADING,
    payload: {
      loadingType: MESSAGE_SUBMIT_ATTEMPT,
      loadingAction: 'set',
      meta: null
    }
  })

  const { payload } = action

  const messagesToSend = yield select(getMessagesToSend)

  yield put({
    type: SET_MESSAGES_TO_SEND,
    payload: [payload, ...messagesToSend]
  })

  const isAwaitingLocation = yield select(getIsAwaitingLocation)

  if (isAwaitingLocation) return

  yield put({
    type: LOCATION_FETCHING
  })
}

/**
 * Clears the message submit loading state.
 */
function * unsetMessageLoading() {
  yield put({
    type: UPDATE_LOADING,
    payload: {
      loadingType: MESSAGE_SUBMIT_ATTEMPT,
      loadingAction: 'unset',
      meta: null
    }
  })
}

/**
 * Sends queued messages without location data when geolocation fetch fails.
 */
function * onLocationFetchFail() {
  const messagesToSend = yield select(getMessagesToSend)

  const auth = yield select(getAuth)

  if (!messagesToSend) return

  const messagesToSendNoLocation = messagesToSend.map(msg => {
    const { message, messageId: message_id, messageGuid } = msg

    return {
      data: {
        ...message,
        message_id
      },
      messageGuid
    }
  })

  yield all(messagesToSendNoLocation.map(({ data, messageGuid }) => {
    return put(sendSuggestionAction({ auth, data, messageGuid }))
  }))
}

/**
 * Sends queued messages with the fetched location coordinates attached.
 */
function * onLocationSet() {
  const auth = yield select(getAuth)
  const location = yield select(getLocation)
  const messagesToSend = yield select(getMessagesToSend)

  if (!messagesToSend.length) return

  const { latitude: lat, longitude: lng } = location

  const messagesWithLocationToSend = messagesToSend.map(msg => {
    const { message, messageId: message_id, messageGuid } = msg

    return {
      data: {
        ...message,
        location: {
          lat, lng
        },
        message_id
      },
      messageGuid
    }
  })
  
  yield all(messagesWithLocationToSend.map(({ data, messageGuid }) => {
    return put(sendSuggestionAction({ auth, data, messageGuid }))
  }))
}


/**
 * Clears loading state, resets the message queue, and resets location after a successful send.
 */
function * onMessageSendSuccess() {
  yield unsetMessageLoading()
  
  yield put({
    type: SET_MESSAGES_TO_SEND,
    payload: []
  })
  yield put({ type: LOCATION_RESET })
}

/**
 * Dispatches the API action to send a single message.
 * @param action
 */
function * onMessageSend(action) {
  const { message, messageGuid, messageId: message_id } = action.payload

  const auth = yield select(getAuth)

  const data = { message_id, ...message }

  yield put(sendSuggestionAction({ auth, data, messageGuid }))
}

/**
 * Clears loading state and shows a warning toast when message send fails.
 */
function * onMessageSendFail() {
  yield unsetMessageLoading()
  
  toast.warn('Send message failed.')
}

/**
 * Root saga that watches for message-related actions.
 */
function * watchMessage() {
  yield takeEvery(ATTEMPT_MESSAGE_SEND_WITH_LOCATION, onAttemptMessageSendWithLocation)
  yield takeEvery(LOCATION_FETCH_FAIL, onLocationFetchFail)
  yield takeEvery(LOCATION_SET, onLocationSet)
  yield takeEvery(ON_MESSAGE_SEND, onMessageSend)
  yield takeEvery(MESSAGE_SEND_FAIL, onMessageSendFail)
  yield takeEvery(MESSAGE_SEND_SUCCESS, onMessageSendSuccess)
}

export default watchMessage
